import { ChangeDetectorRef, Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { take } from 'rxjs';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AppService } from '../../app.service';
import { MoovDefinition } from '../../models/moovDefinition';
import { MoovDefinitionCustomerAdoption } from '../../models/moovDefinitionCustomerAdoption';
import { MoovService } from '../../services/MoovService';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AccountService } from '../../services/AccountService';
import { BillingBucket } from '../../models/billingBucket';

@Component({ standalone:false,
  selector: 'app-edit-adoption-dialog',
  templateUrl: './edit-adoption-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./edit-adoption-dialog.component.scss']
})
export class EditAdoptionDialogComponent {
  title: string = "Edit moov adoption"
  public cancelButtonText = "CANCEL";
  public submitButtonText = "SAVE CHANGES";
  public moovDefinitionCustomerAdoption:MoovDefinitionCustomerAdoption;
  public moovDefinition:MoovDefinition;
  public billingBucketArray:any[] = [];

  public moovDefinitionAdoptionForm:FormGroup;
  public busy=true;

  constructor(@Inject(MAT_DIALOG_DATA) private data: any, private moovService:MoovService, private dialogRef: MatDialogRef<EditAdoptionDialogComponent>, private snackBar: MatSnackBar, 
    private accService:AccountService, private cd:ChangeDetectorRef) {
    this.moovDefinition = data.moovDefinition;
    this.moovDefinitionCustomerAdoption = data.moovDefinitionCustomerAdoption;
    this.moovDefinitionAdoptionForm = new FormGroup({
        usageType: new FormControl<any>(undefined),
        usagePrice: new FormControl<number | undefined>(undefined),
        billingBucket: new FormControl<BillingBucket | undefined>(undefined)
    });
  }

  ngOnInit() {
    
    this.populateForm();
  }

  private populateForm(){
    if(this.moovDefinitionCustomerAdoption && this.moovDefinition){
      this.accService.getBillingBuckets(false).pipe(take(1)).subscribe(bbArray =>{
        this.billingBucketArray = bbArray.slice(); //we also use disabled buckets for now to avoid issues
        this.billingBucketArray.unshift({name:"Default", id:undefined});
        if(this.moovDefinition.billingBucketId) this.billingBucketArray.unshift({name:"Billed to customer", id:0});
        let usage;
        let usagePrice;
        let billingBucket;
        if(this.moovDefinitionCustomerAdoption.usageTypeOverride!=undefined){
          usage = this.usageTypeArray.find(u=>u.value==this.moovDefinitionCustomerAdoption.usageTypeOverride);
        }
        else{
          usage = this.usageTypeArray.find(u=>u.value==this.moovDefinition.usageType);
        }
        if(usage?.value===4){
          if(this.moovDefinitionCustomerAdoption.usagePriceOverride!=undefined){
            usagePrice = this.moovDefinitionCustomerAdoption.usagePriceOverride;
          }
          else{
            usagePrice = this.moovDefinition.usagePrice;
          }
        }
        if(this.moovDefinitionCustomerAdoption.billingBucketIdOverride!=undefined){
          billingBucket = this.billingBucketArray.find(b=>b.id==this.moovDefinitionCustomerAdoption.billingBucketIdOverride);
        }
        else{
          billingBucket = this.billingBucketArray.find(b=>b.id==this.moovDefinition.billingBucketId);
        }
        if(!billingBucket) billingBucket = this.billingBucketArray[0];
        this.moovDefinitionAdoptionForm.get('usageType')!.setValue(usage);
        this.moovDefinitionAdoptionForm.get('usagePrice')!.setValue(usagePrice);
        this.moovDefinitionAdoptionForm.get('billingBucket')!.setValue(billingBucket);
        this.busy=false;
      });
    }
    else this.busy=false;
  }

  get usageTypeArray(){
    return AppService.usageTypeArray;
  }

  get showCustomPriceField(){
    return this.selectedUsageType==4; //unlimited use custom
  }

  get selectedUsageType(){
    return this.moovDefinitionAdoptionForm.get('usageType')!.value?.value;
  }

  get selectedBillingBucket(){
    return this.moovDefinitionAdoptionForm.get('billingBucket')!.value;
  }

  get canSubmit(){
    return !this.busy && this.moovDefinitionAdoptionForm.valid &&
      (this.moovDefinitionAdoptionForm.dirty || (!this.moovDefinitionCustomerAdoption?.usageTypeOverride && this.selectedUsageType != this.moovDefinition.usageType) || (this.moovDefinitionCustomerAdoption?.usageTypeOverride && this.selectedUsageType != this.moovDefinitionCustomerAdoption.usageTypeOverride)
      || (!this.moovDefinitionCustomerAdoption?.billingBucketIdOverride && this.selectedBillingBucket.id != this.moovDefinition.billingBucketId) || (this.moovDefinitionCustomerAdoption?.billingBucketIdOverride && this.selectedBillingBucket.id != this.moovDefinitionCustomerAdoption.billingBucketIdOverride));
  }

  public onUsageTypeChanged($event:any){
    if(this.showCustomPriceField) this.moovDefinitionAdoptionForm.get('usagePrice')!.setValidators([Validators.required]);
    else {
      this.moovDefinitionAdoptionForm.get('usagePrice')!.clearValidators();
      this.moovDefinitionAdoptionForm.get('usagePrice')!.setErrors(null);
      this.moovDefinitionAdoptionForm.get('usagePrice')!.setValue(null);
    }
    this.moovDefinitionAdoptionForm.get('usagePrice')!.updateValueAndValidity();
    this.cd.detectChanges();
  }

  public onSubmitClick(): void {
    this.busy=true;
    if(this.selectedUsageType==this.moovDefinition.usageType && this.moovDefinitionAdoptionForm.get('usagePrice')!.value===this.moovDefinition.usagePrice){
      this.moovDefinitionCustomerAdoption.usageTypeOverride=undefined;
      this.moovDefinitionCustomerAdoption.usagePriceOverride=undefined;
    }
    else{
      this.moovDefinitionCustomerAdoption.usageTypeOverride=this.selectedUsageType;
      if(this.selectedUsageType===4) this.moovDefinitionCustomerAdoption.usagePriceOverride=this.moovDefinitionAdoptionForm.get('usagePrice')!.value;
    }
    if(this.selectedBillingBucket.id===this.moovDefinition.billingBucketId){
      this.moovDefinitionCustomerAdoption.billingBucketIdOverride=undefined;
    }
    else{
      this.moovDefinitionCustomerAdoption.billingBucketIdOverride=this.selectedBillingBucket.id;
    }
    this.moovService.updateMoovDefinitionCustomerAdoption(this.moovDefinitionCustomerAdoption).pipe(take(1)).subscribe(r=>{
      if(r.success){
        this.snackBar.open("Adoption configuration updated", '×', { panelClass: ['primary-snackbar'], verticalPosition: 'top', duration: 3000 });
        this.dialogRef.close(true);
      }
      else{
        this.snackBar.open("Error updating adoption configuration: "  + r.message, 'x', { panelClass: ['warn-snackbar'], verticalPosition: 'top' });
      }
      this.busy=false;
      this.cd.detectChanges();
    });
    
  }

  public onCancelClick(): void {
    this.dialogRef.close(false);
  }

}

